import lodashCloneDeep from 'lodash/cloneDeep';
import lodashOmit from 'lodash/omit';
import lodashValues from 'lodash/values';
import {
  FILTER_DATA_CHANGED,
  INITIALIZE_UNDERLYING_METRICS,
  SET_HTML_TEXT,
  FILTER_SELECTED_VALUE_CHANGED,
  FILTER_DATA_RESET,
  METRICS_CHANGED,
  CHANGE_AGGREGATE,
  CHANGE_METRIC_ALIAS,
  SET_CHART_TYPE,
  CHART_OPTION_VALUE_CHANGED,
  HAVING_INITIALIZE_FILTERS,
  HAVING_FILTER_SELECTED_VALUE_CHANGED,
  HAVING_FILTER_DATA_RESET,
  CHANGE_HAVING_AGGREGATE,
  SET_CUSTOM_COLUMNS_GLOBAL,
  SUMMARISATION_LIST_CHANGED,
  SUMMARISATION_LIST_RESET,
  METRICS_RESET,
  INITIALIZE_FILTERS,
  GENERIC_INITIALIZE_FILTERS,
  GENERIC_FILTER_DATA_RESET,
  SET_REPORT_MASTER_DATA,
  SET_CUSTOM_COLUMNS_LOCAL,
  SORT_SELECTED_LIST_CHANGED,
} from 'src/reduxActions/actionNameEnums';
import { buildObjectFromCustomColumnObj } from './dataSetupContainerPart/customColumnsDrawer';
import { getDownloadRowLimitWarningMessage } from './chartBuilderUtils';

/**
 * Process AI-generated filters into the format expected by the application
 */
export const processAIFilters = (aiFilters, selectedReport, reportConfig: any = null) => {
  return aiFilters.map(filter => {
    const fieldName = filter.field || filter.column || filter.metric;
    
    const nestedFilter = filter.filter || {};
    
    let selectedFilter = filter.operator || filter.selectedFilter || nestedFilter.name;
    if (!selectedFilter) {
      const dataType = filter.dataType || filter.type || nestedFilter.type || 'text';
      selectedFilter = dataType === 'date' || dataType === 'timestampz' ? 'dateIsEqualTo' : 'textIsEqualTo';
    }
    
    let data: any = nestedFilter.data || filter.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        data = null;
      }
    }

    let dataType = filter.dataType || filter.type || nestedFilter.type;
    
    if (!dataType && reportConfig) {
      const metric = reportConfig.metrics?.find(m => m.metric === fieldName || m.value === fieldName);
      if (metric) {
        dataType = metric.type;
      } else {
        const underlyingMetric = reportConfig.underlyingMetrics?.find(m => m.metric === fieldName || m.value === fieldName);
        if (underlyingMetric) {
          dataType = underlyingMetric.type;
        }
      }
    }
    
    if (!dataType) {
      dataType = 'text';
    }

    const processedFilter = {
      value: fieldName || filter.value,
      prettyName: filter.displayName || filter.prettyName || nestedFilter.prettyName || fieldName,
      type: filter.dataType || filter.type || nestedFilter.type || 'text',
      selectedFilter: selectedFilter,
      ...(data && Object.keys(data).length > 0 ? { data: data } : {}),
      searchOptions: filter.searchOptions || [],
      havingOperation: filter?.filter?.havingOperation,
      havingOperations: filter.havingOperations || [],
      caseInsensitive: filter.caseInsensitive || false,
      includeChildren: filter.includeChildren || false,
      holidays: filter.holidays || [],
      section: filter.section || null,
      sql: filter.sql || nestedFilter.sql || '',
      isDefaultFilter: filter.isDefaultFilter || nestedFilter.isDefaultFilter || false,
      filterMetaData: {
        required: filter.isDefaultFilter || nestedFilter.isDefaultFilter || false,
      },
      dashboardFilterType: filter.dashboardFilterType,
      filterId: filter.filterId || `${selectedReport}.${fieldName}`,
      filterPriority: filter.filterPriority || 10,
      overrideOtherFilters: filter.overrideOtherFilters || false,
    };
    
    return processedFilter;
  });
};

/**
 * Process AI-generated metrics into the format expected by the application
 */
export const processAIMetrics = (aiMetrics) => {
  return aiMetrics.map((metric) => {
    const fieldName = metric.field || metric.column || metric.metric;
    const newMetric = lodashCloneDeep({ 
      ...metric, 
      value: fieldName,
      prettyName: metric.displayName || metric.prettyName || metric.alias || fieldName,
      type: metric.dataType || metric.type || 'number',
      alias: metric.alias || metric.displayName,
      operation: metric.aggregation || metric.operation || 'sum',
      name: fieldName,
    });

    const groupBy = { ...newMetric.groupBy };
    if (metric?.groupBy?.bucketValue) {
      groupBy.bucketValue = metric.groupBy.bucketValue;
    }
    if (metric?.groupBy?.toApply) {
      groupBy.toApply = metric.groupBy.toApply;
    }
    
    newMetric.groupBy = groupBy;

    if (newMetric?.linkedReport?.linkedMetrices && typeof newMetric.linkedReport.linkedMetrices === 'string') {
      try {
        newMetric.linkedReport.linkedMetrices = JSON.parse(newMetric.linkedReport.linkedMetrices);
      } catch (e) {
      }
    }

    if (newMetric?.dummyData && typeof newMetric.dummyData === 'string') {
      try {
        newMetric.dummyData = JSON.parse(newMetric.dummyData);
      } catch (e) {
      }
    }
    return newMetric;
  });
};

/**
 * Process AI-generated custom metrics into the format expected by the application
 */
export const processAICustomMetrics = (aiCustomMetrics) => {
  return aiCustomMetrics.map((metric) => {
    const newMetric = lodashCloneDeep(metric);

    const fieldName = newMetric.field || newMetric.column || newMetric.metric || newMetric.name || newMetric.prettyName;
    
    newMetric.name = newMetric.name || fieldName;
    newMetric.formula = newMetric.formula || newMetric.expression || newMetric.calculation;
    newMetric.alias = newMetric.alias || newMetric.displayName || newMetric.prettyName || fieldName;
    newMetric.type = newMetric.type || newMetric.dataType || 'derived';
    
    if (newMetric?.linkedReport?.linkedMetrices && typeof newMetric.linkedReport.linkedMetrices === 'string') {
      try {
        newMetric.linkedReport.linkedMetrices = JSON.parse(newMetric.linkedReport.linkedMetrices);
      } catch (e) {
      }
    }

    if (newMetric?.dummyData && typeof newMetric.dummyData === 'string') {
      try {
        newMetric.dummyData = JSON.parse(newMetric.dummyData);
      } catch (e) {
      }
    }


    return newMetric;
  });
};

/**
 * Process AI-generated aggregate metrics into the format expected by the application
 */
export const processAIAggregateMetrics = (aiAggregateMetrics) => {
  return aiAggregateMetrics.map((metric) => {
    const newMetric = lodashCloneDeep({
      ...metric,
      value: metric.field || metric.column,
      prettyName: metric.prettyName || metric.field || metric.column,
      type: metric.dataType || 'number',
      aggregate: metric.aggregation || 'sum',
      id: metric.id || `custom_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    });

    if (newMetric?.linkedReport?.linkedMetrices && typeof newMetric.linkedReport.linkedMetrices === 'string') {
      try {
        newMetric.linkedReport.linkedMetrices = JSON.parse(newMetric.linkedReport.linkedMetrices);
      } catch (e) {
      }
    }

    if (newMetric?.dummyData && typeof newMetric.dummyData === 'string') {
      try {
        newMetric.dummyData = JSON.parse(newMetric.dummyData);
      } catch (e) {
      }
    }
    return lodashOmit(newMetric, ['field', 'column']);
  });
};

/**
 * Populate Redux state with AI-generated data based on API response
 */
export const populateReduxWithAiData = (dispatch, filtersMetaData = null, selectedReport = 'consignmentReport', 
  reportConfig: any = null, state: any = null) => 
  (api: string, result: any) => {
  switch (api) {
    case 'generateFilters':
      const filtersData = Array.isArray(result) ? result : (Array.isArray(result.data) ? result.data : []);
      
      if (filtersData.length > 0) {
        const processedFilters = processAIFilters(filtersData, selectedReport, reportConfig);
        const validatedFilters = processedFilters.map(filter => {
          const validated = { ...filter };
          
          if (!validated.selectedFilter) {
            validated.selectedFilter = validated.type === 'date' || validated.type === 'timestampz' ? 'dateIsEqualTo' : 'textIsEqualTo';
          }
          
          // const filterTypesWithoutData = ['noInput'];
          // const relativeTimeFilters = ['timestampzPrevious', 'timestampzNext', 'dateIsInTheLast', 'dateIsInTheNext'];
          
          // if (!validated.data && 
          //     !filterTypesWithoutData.includes(validated.type) &&
          //     !relativeTimeFilters.includes(validated.selectedFilter)) {
          //   return null;
          // }
          return validated;
        }).filter(filter => filter !== null);

        const filterObject = {};
        validatedFilters.forEach(filter => {
          filterObject[filter.value] = filter;
        });

        dispatch({
          type: FILTER_DATA_CHANGED,
          payload: {
            metricData: validatedFilters,
            selectedReport: selectedReport,
            filtersMetaData,
          },
        });

        const filterDataArray: any[] = [];
        
        validatedFilters.forEach(filter => {
          if (filter.value) {
              let dispatchData;
              
              dispatchData = { value: filter.data};
              if (filter.type === 'number' || filter.type === 'boolean' || filter.type === 'text') {
                dispatchData = filter.data;
              }else if (filter.data.value && filter.type === 'timestampz') {
                dispatchData = filter.data;
              } else {
                dispatchData = { value: {
                  value: filter.data,
                }};
              }
            
            filterDataArray.push({
              metricsName: filter.value,
              data: dispatchData,
            });
          }
        });

        if (filterDataArray.length > 0) {
          dispatch({
            type: FILTER_SELECTED_VALUE_CHANGED,
            payload: {
              data: filterDataArray,
            },
          });
        }
      }
      break;

    case 'generateMetrics':
      const metricsData = Array.isArray(result) ? result : (Array.isArray(result.data) ? result.data : []);
      
      if (metricsData.length > 0) {
        const processedMetrics = processAIMetrics(metricsData);

        const regularMetrics: any[] = [];
        const summarizationMetrics: any[] = [];
        const sortByMetrics: any[] = [];
        processedMetrics.forEach((metric: any) => {
          if (metric.groupBy?.toApply === true) {
            const summarizationMetric = {
              ...metric,
              section: null,
            };
            delete summarizationMetric.operation;
            summarizationMetrics.push(summarizationMetric);
            regularMetrics.push(summarizationMetric);
          } 
          if (metric.sortBy) {
            sortByMetrics.push(metric);
          }
          regularMetrics.push(metric);

        });

        if (regularMetrics.length > 0) {
          const metricsObject: any =  {};
          regularMetrics.forEach((metric: any) => {
            metricsObject[metric.value] = metric;
          });
          dispatch({
            type: METRICS_CHANGED,
            payload: metricsObject,
          });

          regularMetrics.forEach((metric: any) => {
            if (metric.operation) {
              dispatch({
                type: CHANGE_AGGREGATE,
                payload: {
                  name: metric.value,
                  value: metric.operation,
                },
              });
            }

            if (metric.alias) {
              dispatch({
                type: CHANGE_METRIC_ALIAS,
                payload: {
                  name: metric.value,
                  value: metric.alias,
                },
              });
            }
          });
        }

        if (summarizationMetrics.length > 0) {
          dispatch({
            type: SUMMARISATION_LIST_CHANGED,
            payload: summarizationMetrics,
          });
        }

        if (sortByMetrics.length > 0) {
          dispatch({
            type: SORT_SELECTED_LIST_CHANGED,
            payload: sortByMetrics,
          });
        }
      }
      break;

    case 'generateChartOptions':
      const chartOptionsData = Array.isArray(result) ? result : (Array.isArray(result.data) ? result.data : []);
      
      if (chartOptionsData.length > 0) {
        chartOptionsData.forEach(chartOptionGroup => {
          if (chartOptionGroup.chartType) {
            dispatch({
              type: SET_CHART_TYPE,
              payload: {
                chartType: chartOptionGroup.chartType,
              },
            });
          }

          if (chartOptionGroup.chartOptions && Array.isArray(chartOptionGroup.chartOptions)) {
            chartOptionGroup.chartOptions.forEach(chartOption => {
              try {
                const parsedData = JSON.parse(chartOption.data);
                const optionValue = parsedData.value;
                
                dispatch({
                  type: CHART_OPTION_VALUE_CHANGED,
                  payload: {
                    optionName: chartOption.option,
                    value: optionValue,
                    section: chartOption.section,
                  },
                });
                    } catch (e) {
      }
            });
          }
        });
      }
      break;

    case 'generateHavingFilters':
      const havingFiltersData = Array.isArray(result) ? result : (Array.isArray(result.data) ? result.data : []);

      if (havingFiltersData.length > 0) {
        const processedHavingFilters = processAIFilters(havingFiltersData[0].havingFilters || havingFiltersData, selectedReport);

        dispatch({
          type: HAVING_INITIALIZE_FILTERS,
          payload: {
            metricData: processedHavingFilters,
            filtersMetaData,
          },
        });

        processedHavingFilters.forEach(filter => {
          if (filter.value && filter.data) {
            dispatch({
              type: HAVING_FILTER_SELECTED_VALUE_CHANGED,
              payload: {
                metricsName: filter.value,
                data: filter.data,
              },
            });
          }

          if (filter.havingOperation) {
            dispatch({
              type: CHANGE_HAVING_AGGREGATE,
              payload: {
                name: filter.value,
                value: filter.havingOperation,
              },
            });
          }
        });
      }
      break;

    case 'generateCustomMetrics':
      let customMetricsData;
      if (Array.isArray(result)) {
        customMetricsData = result.reduce((acc, item) => {
          if (item.data && Array.isArray(item.data)) {
            return acc.concat(item.data);
          }
          return acc;
        }, []);
      } else if (result && Array.isArray(result.data)) {
        customMetricsData = result.data;
      } else {
        customMetricsData = [];
      }
      
      if (customMetricsData.length > 0) {
        const processedCustomMetrics = processAICustomMetrics(customMetricsData);

        const customMetricsWithIds = processedCustomMetrics.map(customMetric => ({
          ...customMetric,
          uniqueId: `custom_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        }));

        customMetricsWithIds.forEach(customMetric => {
          const customMetricPayload = {
            id: customMetric.uniqueId,
            prettyName: customMetric.prettyName,
            formula: customMetric.formula,
            alias: customMetric.alias,
            type: customMetric.type,
            ...customMetric,
          };

          dispatch({
            type: SET_CUSTOM_COLUMNS_GLOBAL,
            payload: {
              name: customMetricPayload.id,
              body: customMetricPayload,
            },
          });
        });

        const customMetricsForReportConfig = customMetricsWithIds.map(customMetric => {
          const customMetricForConfig = {
            id: customMetric.uniqueId,
            prettyName: customMetric.prettyName,
            formula: customMetric.formula,
            alias: customMetric.alias,
            type: customMetric.type,
            name: customMetric.prettyName,
            value: customMetric.prettyName,
            ...customMetric,
          };
          
          return buildObjectFromCustomColumnObj(customMetricForConfig, 'global');
        });

        if (reportConfig?.reports?.[selectedReport]?.metrics) {
          const existingMetrics = reportConfig.reports[selectedReport].metrics.filter(
            item => !item.customColumnType || item.customColumnType !== 'global',
          );
          
          const updatedMetrics = [...existingMetrics, ...customMetricsForReportConfig];
          
          const updatedReportConfig = {
            ...reportConfig,
            reports: {
              ...reportConfig.reports,
              [selectedReport]: {
                ...reportConfig.reports[selectedReport],
                metrics: updatedMetrics,
              },
            },
          };

          dispatch({
            type: SET_REPORT_MASTER_DATA,
            payload: updatedReportConfig,
          });
        }
      }
      break;

      case 'generateCustomAggregateMetrics':
        const aggregateMetricsData = Array.isArray(result) ? result : (Array.isArray(result.data) ? result.data : []);
        
        if (aggregateMetricsData.length > 0) {
          const processedAggregateMetrics = processAIAggregateMetrics(aggregateMetricsData);
  
          const aggregateMetricsForReportConfig = processedAggregateMetrics.map(aggregateMetric => {
            const aggregateMetricForConfig = {
              id: aggregateMetric.id,
              prettyName: aggregateMetric.prettyName,
              formula: aggregateMetric.formula,
              alias: aggregateMetric.alias,
              type: aggregateMetric.type,
              name: aggregateMetric.prettyName,
              value: aggregateMetric.prettyName,
              ...aggregateMetric,
            };
            
            const body = buildObjectFromCustomColumnObj(aggregateMetricForConfig, 'local');
  
            return { body, name: aggregateMetric.id };
          });

          dispatch({
            type: SET_CUSTOM_COLUMNS_LOCAL,
            payload: aggregateMetricsForReportConfig,
          });
  
          if (state) {
            const metricData = { ...(state.metrics || {}) };  
            const metric = lodashValues(metricData);
            const toRemoveMetric = metric.filter(item => item.customColumnType === 'local');

            toRemoveMetric.forEach(item => {
                delete metricData[item.value];
            });
            
            aggregateMetricsForReportConfig.forEach(item => {
                metricData[item.name] = {
                    ...item,
                    customColumnType: 'local',
                };
            });

            dispatch({
                type: METRICS_CHANGED,
                payload: metricData,
            });
          }
        }
        break;
    default:
  }
};

/**
 * Reset Redux state to default values
 */
export const resetStateToDefault = (dispatch, filtersMetaData = null, selectedReport = 'consignmentReport', metrics: any[] = []) => {
  dispatch({
    type: FILTER_DATA_RESET,
  });

  dispatch({
    type: GENERIC_FILTER_DATA_RESET,
  });
}; 